import { useState, useEffect, useCallback } from 'react';
import { Timestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

import { LIVE } from '@/util/constants';

const buildQueryString = (params) =>
  Object.entries(params)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

const fetchPosts = async (queryStr) => {
  const auth = getAuth();
  const user = auth.currentUser;
  const idToken = user ? await user.getIdToken() : null;

  const response = await fetch(`/api/posts?${queryStr}`, {
    headers: {
      Authorization: idToken ? `Bearer ${idToken}` : '',
    },
  });

  if (!response.ok) throw new Error('Failed to fetch posts');
  return response.json();
};

/**
 * @param {Object} options
 * @param {string | null} [options.userId]
 * @param {string} [options.status]
 * @param {string} [options.orderBy]
 * @param {string} [options.orderDirection]
 * @param {number | null} [options.limit]
 * @param {boolean} [options.enablePagination]
 * @param {boolean} [options.enabled]
 */
export const useGetPosts = ({
  userId = null,
  status = LIVE,
  orderBy = 'createdAt',
  orderDirection = 'desc',
  limit = null,
  enablePagination = false,
  enabled = true,
}) => {
  const [posts, setPosts] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);

  // Fetch initial posts
  useEffect(() => {
    if (!enabled) return;

    const loadInitialPosts = async () => {
      setIsLoading(true);

      try {
        const queryStr = buildQueryString({ userId, status, orderBy, orderDirection, limit });
        const data = await fetchPosts(queryStr);
        setPosts(data);

        if (enablePagination && data.length > 0) {
          const lastPost = data[data.length - 1];
          const lastCursor = lastPost.approvedAt?.seconds || lastPost.createdAt?.seconds || null;

          setCursor(lastCursor);
          setHasMore(data.length === limit);
        } else {
          setHasMore(false);
        }
      } catch (error) {
        console.error(error);
        setHasMore(false);
      }

      setIsLoading(false);
    };

    loadInitialPosts();
  }, [enabled, userId, status, orderBy, orderDirection, limit, enablePagination]);

  // Load more function for pagination
  const loadMore = useCallback(async () => {
    if (!enablePagination || !hasMore || isLoading || !cursor) return;

    setIsLoading(true);

    try {
      const queryStr = buildQueryString({
        userId,
        status,
        orderBy,
        orderDirection,
        limit,
        startAfter: cursor,
      });

      const data = await fetchPosts(queryStr);
      setPosts((prevPosts) => [...prevPosts, ...data]);

      if (data.length > 0) {
        const lastPost = data[data.length - 1];
        const lastCursor = lastPost.approvedAt?.seconds || lastPost.createdAt?.seconds || null;
        setCursor(lastCursor);
        setHasMore(data.length === limit);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error(error);
      setHasMore(false);
    }

    setIsLoading(false);
  }, [cursor, enablePagination, hasMore, isLoading, userId, status, orderBy, orderDirection, limit]);

  return {
    posts,
    isLoading,
    hasMore,
    loadMore,
  };
};
